'use es6';

import SeamlessImmutable from 'seamless-immutable';
import { createSelector } from 'reselect';
import { getIsInLockedFolderFactory } from 'DesignData/selectors/finder';
import * as RawAssetExtensions from 'DesignData/enums/RawAssetExtensions';
import { UNINITIALIZED, PENDING } from 'DesignData/enums/RequestStatus';
export const getRawAssetsById = state => state.rawAssetBuffers.byId;

const getHasBufferedChangesById = state => state.rawAssetBuffers.hasBufferedChangesById;

export const getUploadPreviewUrlById = state => state.rawAssetBuffers.uploadPreviewUrlById;
export const getUploadProgressById = state => state.rawAssetBuffers.uploadProgressById;
export const getRawAssetId = (state, props) => props.assetId || props.rawAssetId;

const getRevisionId = (state, props) => props.revisionId;

const getRevisionsById = state => state.rawAssetRevisions.byId;

const getRawAssetRevision = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  const revisionId = getRevisionId(state, props);
  const revisionsById = getRevisionsById(state);
  const revisionsByRevisionId = revisionsById[rawAssetId] && revisionsById[rawAssetId].revisions;
  return revisionId && revisionsByRevisionId ? revisionsByRevisionId[revisionId] : null;
};

export const getRawAsset = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  const rawAssetsById = getRawAssetsById(state, props);
  const rawAssetRevision = getRawAssetRevision(state, props);
  return rawAssetRevision || rawAssetsById[rawAssetId];
};
export const getRawAssetPath = (state, props) => {
  const rawAssetsById = getRawAssetsById(state);
  const rawAssetId = getRawAssetId(state, props);
  return rawAssetsById[rawAssetId] && rawAssetsById[rawAssetId].path;
};
export const getRawAssetSize = (state, props) => {
  const asset = getRawAsset(state, props);
  return asset && typeof asset.size === 'number' && asset.size;
};
export const getRawAssetDimensions = (state, props) => {
  const asset = getRawAsset(state, props);
  return asset && typeof asset.width === 'number' && typeof asset.height === 'number' && {
    width: asset.width,
    height: asset.height
  };
};
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getFetchStatus = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  return state.rawAssetBuffers.fetchStatusById[rawAssetId] || UNINITIALIZED;
};
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getPublishStatus = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  return state.rawAssetBuffers.publishStatusById[rawAssetId] || UNINITIALIZED;
};
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getSaveStatus = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  return state.rawAssetBuffers.saveStatusById[rawAssetId] || UNINITIALIZED;
};
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getFetchError = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  return state.rawAssetBuffers.fetchErrorById[rawAssetId];
};
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getSaveError = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  return state.rawAssetBuffers.saveErrorById[rawAssetId];
};
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getPublishError = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  return state.rawAssetBuffers.publishErrorById[rawAssetId];
};
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getRequestStatus = createSelector([getFetchError, getPublishError, getSaveError, getFetchStatus, getPublishStatus, getSaveStatus], (fetchError, publishError, saveError, fetchStatus, publishStatus, saveStatus) => {
  return SeamlessImmutable({
    fetchError,
    saveError,
    publishError,
    cloneError: null,
    moveRenameError: null,
    fetchStatus,
    publishStatus,
    saveStatus,
    isFetching: fetchStatus === PENDING,
    isPublishing: publishStatus === PENDING,
    isSaving: saveStatus === PENDING
  });
});
/**
 * Required props:
 *  - assetId || rawAssetId
 */

export const getHasBufferedChanges = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  return getHasBufferedChangesById(state)[rawAssetId] || false;
};
export const makeGetIsRawAssetLocked = () => createSelector([getRawAsset, getIsInLockedFolderFactory], (rawAsset, getIsInLockedFolder) => {
  if (!rawAsset) {
    return true;
  }

  return getIsInLockedFolder(rawAsset.folderId);
});
export const makeGetIsReadOnly = () => {
  const getIsRawAssetLocked = makeGetIsRawAssetLocked();
  return createSelector([getIsRawAssetLocked, getRawAsset], (isRawAssetLocked, rawAsset) => {
    if (isRawAssetLocked) return true;
    if (rawAsset.readOnly) return true;
    return false;
  });
};
export const makeGetIsEditableType = () => {
  return createSelector([getRawAsset], (rawAsset = {}) => {
    const extensionConfig = RawAssetExtensions[rawAsset && rawAsset.extension];

    if (extensionConfig && extensionConfig.editable) {
      return true;
    }

    return false;
  });
};
export const getUploadPreviewUrl = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  const uploadPreviewUrlById = getUploadPreviewUrlById(state);
  return uploadPreviewUrlById[rawAssetId];
};
export const getUploadProgress = (state, props) => {
  const rawAssetId = getRawAssetId(state, props);
  const uploadProgressById = getUploadProgressById(state);
  return uploadProgressById[rawAssetId] || 0;
};
export const getIsStaged = (state, props) => {
  const asset = getRawAsset(state, props);
  return asset && asset.staged;
};